'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Lesson {
  title: string;
  duration: string;
  type: string;
}

interface Module {
  id: number;
  title: string;
  description: string;
  duration: string;
  lessons: Lesson[];
}

interface CurriculumSectionProps {
  modules: Module[];
}

export default function CurriculumSection({ modules }: CurriculumSectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [expandedModule, setExpandedModule] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const toggleModule = (moduleId: number) => {
    if (!isHydrated) return;
    setExpandedModule(expandedModule === moduleId ? null : moduleId);
  };

  return (
    <section id="curriculum" className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
            Comprehensive Curriculum
          </h2>
          <p className="text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Master industry-relevant skills through our structured, project-based learning modules designed by experts.
          </p>
        </div>

        <div className="space-y-4">
          {modules.map((module) => (
            <div
              key={module.id}
              className="bg-card rounded-xl border border-border overflow-hidden shadow-sky-sm hover:shadow-sky-md transition-all duration-300"
            >
              <button
                onClick={() => toggleModule(module.id)}
                className="w-full px-6 py-5 flex items-center justify-between hover:bg-surface/50 transition-colors duration-300"
              >
                <div className="flex items-center space-x-4 flex-1 text-left">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-headline font-bold text-primary">
                      {module.id}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-headline font-semibold text-foreground mb-1">
                      {module.title}
                    </h3>
                    <p className="text-sm text-muted-foreground font-body">
                      {module.description}
                    </p>
                  </div>
                  <div className="flex items-center space-x-4 flex-shrink-0">
                    <div className="flex items-center space-x-2">
                      <Icon name="ClockIcon" size={16} variant="outline" className="text-muted-foreground" />
                      <span className="text-sm text-muted-foreground font-medium">
                        {module.duration}
                      </span>
                    </div>
                    <Icon
                      name="ChevronDownIcon"
                      size={20}
                      variant="outline"
                      className={`text-muted-foreground transition-transform duration-300 ${
                        expandedModule === module.id ? 'rotate-180' : ''
                      }`}
                    />
                  </div>
                </div>
              </button>

              {expandedModule === module.id && (
                <div className="px-6 pb-6 border-t border-border">
                  <div className="pt-4 space-y-3">
                    {module.lessons.map((lesson, lessonIndex) => (
                      <div
                        key={lessonIndex}
                        className="flex items-center justify-between p-4 bg-surface rounded-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <Icon
                            name={lesson.type === 'video' ? 'PlayCircleIcon' : lesson.type === 'quiz' ? 'DocumentTextIcon' : 'CodeBracketIcon'}
                            size={20}
                            variant="outline"
                            className="text-primary"
                          />
                          <span className="text-sm font-medium text-foreground">
                            {lesson.title}
                          </span>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          {lesson.duration}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}